import TableCell from "@mui/material/TableCell"
import { format } from "date-fns"

import Column from "../../../common/components/Column"
import { adminApi } from "../axios"
import AdminCallingNumberDialogHost from "../components/AdminCallingNumberDialogHost"
import AdminCallingNumberRowActions from "../components/AdminCallingNumberRowActions"
import AdminExportCsvButton from "../components/AdminExportCsvButton"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminCallingNumberMutations } from "../hooks/useAdminCallingNumberMutations"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"

export const CALLING_NUMBER_FILTER_SPECS = {
  concierge: { param: "concierge_user_id" },
  account: { param: "owner_user_id" },
  greeting: { param: "has_greeting" },
}

const TOOLBAR_FILTERS = [
  { key: "concierge", label: "Concierge", type: "entity", entity: "concierge" },
  {
    key: "account",
    label: "Parent account",
    type: "entity",
    entity: "account",
    placeholder: "Type 2+ letters",
  },
  {
    key: "greeting",
    label: "Greeting",
    type: "select",
    options: [
      { value: "true", label: "Has greeting" },
      { value: "false", label: "No greeting" },
    ],
  },
]

export default function AdminConciergeCallingNumbers() {
  const query = useAdminPaginatedQuery(adminApi.fetchCallingNumbers, {
    filters: CALLING_NUMBER_FILTER_SPECS,
    defaultSortColumn: "created",
    defaultSortDirection: "desc",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
    exportParams,
  } = query

  const mutations = useAdminCallingNumberMutations(() => refresh())

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Twilio calling numbers"
        subtitle="Concierge caller IDs per parent account — edit E.164 or release like Django admin."
      />
      <AdminListToolbar
        query={query}
        searchLabel="Search number, concierge or parent account"
        noun={totalResultsSize === 1 ? "number" : "numbers"}
        filters={TOOLBAR_FILTERS}
        actions={
          <AdminExportCsvButton
            exportFn={adminApi.exportCallingNumbers}
            params={exportParams}
            filenamePrefix="concierge-calling-numbers"
          />
        }
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={7}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="twilio_voice_phone"
              label="Number"
              query={query}
              minWidth={{ xs: 80, sm: 96 }}
            />
            <AdminSortableHeaderCell
              columnName="concierge"
              label="Concierge"
              query={query}
              minWidth={{ xs: 88, sm: 112 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
              Parent account
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
              Greeting
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="created"
              label="Created"
              query={query}
              minWidth={{ xs: 72, sm: 88 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
              SID
            </AdminHeaderTableCell>
            <AdminHeaderTableCell align="right" minWidth={{ xs: 64, sm: 80 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((row) => (
          <AdminTableRow key={row.id} hover>
            <AdminTruncatedCell value={row.twilio_voice_phone} />
            <AdminTruncatedCell
              value={row.concierge_name || row.concierge_email}
            />
            <AdminTruncatedCell value={row.owner_name || row.owner_email} />
            <AdminTruncatedCell value={row.has_greeting ? "Yes" : "No"} />
            <AdminTruncatedCell
              value={
                row.created ? format(new Date(row.created), "MMM d, yyyy") : "—"
              }
            />
            <AdminTruncatedCell value={row.twilio_phone_sid} />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <AdminCallingNumberRowActions
                row={row}
                onEdit={mutations.setEditRow}
                onDelete={mutations.setDeleteRow}
              />
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>

      <AdminCallingNumberDialogHost
        editRow={mutations.editRow}
        deleteRow={mutations.deleteRow}
        saving={mutations.saving}
        dialogError={mutations.dialogError}
        onClose={mutations.closeDialogs}
        onSave={mutations.handleSaveEdit}
        onConfirmDelete={mutations.handleConfirmDelete}
      />
    </Column>
  )
}
